<template lang="pug">
.study-bulk(:data-header-opened='isHeaderOpened', :data-vertical='isVertical')
  //- ヘッダー部
  study-header.study-bulk__header(:returnEvent='returnButtonEvent', :isFirstTime='isFirstTime', @change-toggle='changeHeaderToggle')
    template(#userUidTitle)
      slot(name='userUidTitle')
    template(#userNameTitle)
      slot(name='userNameTitle')
    template(#returnText)
      slot(name='returnButtonText')
  //- コンテンツ部
  .study-bulk__content
    //-  ナビゲーション部
    .study-bulk__content__navigation
      study-navigation.study-bulk__content__navigation__body(
        :studyTitle='studyTitle',
        :currentTime='currentTime',
        :remainingTime='remainingTime',
        :isVertical='isVertical',
        :showNextButton='showNextButton',
        :showPrevButton='showPrevButton',
        :showMenuButton='false',
        :nextButtonEvent='nextButtonEvent',
        :prevButtonEvent='prevButtonEvent'
      )
        template(#currentTimeLabel)
          slot(name='currentTimeLabel')
        template(#remainingTimeLabel)
          slot(name='remainingTimeLabel')
    //- 解答部
    .study-bulk__content__body
      component.study-bulk__content__body__answer(
        :is='answerComponent',
        :isChangingProblem='isChangingProblem',
        :studyProblems='studyProblems',
        :mathKeyboardName='mathKeyboardName',
        :showNextButton='showNextButton',
        :nextEvent='nextButtonEvent',
        :completeEvent='completeButtonEvent'
      )
        template(#problemNumberBeforeLabel)
          slot(name='problemNumberBeforeLabel')
        template(#problemNumberAfterLabel)
          slot(name='problemNumberAfterLabel')
        template(#tegakiResultTitle)
          slot(name='tegakiResultTitle')
        template(#tegakiRecognizeButtonLabel)
          slot(name='tegakiRecognizeButtonLabel')
        template(#nextButtonLabel)
          slot(name='bulkNextButtonLabel')
        template(#completeButtonLabel)
          slot(name='bulkCompleteButtonLabel')
  //- 完了確認モーダル
  modal-study-confirm.study-bulk__modal(
    ref='modalStudyConfirm',
    :notAnsweredNumbers='notAnsweredNumbers',
    :submitEvent='doStudyComplete'
  )
    template(#studyConfirmTitle)
      slot(name='studyConfirmTitle')
    template(#notAnsweredLabel)
      slot(name='notAnsweredLabel')
    template(#studyConfirmText1)
      slot(name='studyConfirmText1')
    template(#studyConfirmText2)
      slot(name='studyConfirmText2')
    template(#submitText)
      slot(name='submitText')
    template(#hideText)
      slot(name='hideText')
  //- 中断確認モーダル
  modal-study-confirm.study-bulk__modal(ref='modalStudySuspend', :submitEvent='moveReturnEvent')
    template(#studyConfirmTitle)
      slot(name='studySuspendTitle')
    template(#studyConfirmText1)
      slot(name='studySuspendText1')
    template(#studyConfirmText2)
      slot(name='studySuspendText2')
    template(#submitText)
      slot(name='submitText')
    template(#hideText)
      slot(name='hideText')
  //- 完了モーダル
  modal-study-complete.study-bulk__modal(ref='modalStudyComplete', :submitEvent='studyEndEvent')
    template(#studyCompleteTitle)
      slot(name='studyCompleteTitle')
    template(#submitText)
      slot(name='completeSubmitText')
</template>

<script lang="ts">
import { Component, Prop, Vue, Ref } from 'vue-property-decorator'
import StudyHeader from '@/components/organisms/student/StudyHeader.vue'
import StudyNavigation from '@/components/organisms/student/StudyNavigation.vue'
import StudyBulkAnswerHorizontal from '@/components/organisms/student/StudyBulkAnswerHorizontal.vue'
import StudyBulkAnswerVertical from '@/components/organisms/student/StudyBulkAnswerVertical.vue'
import ModalStudyConfirm from '@/components/organisms/student/ModalStudyConfirm.vue'
import ModalStudyComplete from '@/components/organisms/student/ModalStudyComplete.vue'
import { StudyProblem } from '@/models/studyModel'

/**
 * テスト画面（一括解答）
 */
@Component({
  components: {
    StudyHeader,
    StudyNavigation,
    StudyBulkAnswerHorizontal,
    StudyBulkAnswerVertical,
    ModalStudyConfirm,
    ModalStudyComplete,
  },
})
export default class StudyBulk extends Vue {
  // ヘッダーの開閉情報
  private isHeaderOpened = false

  // 学習タイトル
  @Prop()
  studyTitle!: string

  // 実施時間
  @Prop()
  currentTime!: string

  // 残り時間
  @Prop()
  remainingTime!: string

  // 問題切替中フラグ(主に問題を切り替えるタイミングで、コンポーネントを再描画する場合に用いる)
  @Prop({ default: false })
  isChangingProblem!: boolean

  // 縦書きフラグ
  @Prop()
  isVertical!: boolean

  // 初回フラグ
  @Prop()
  isFirstTime!: Boolean

  // 次へボタン押下時イベント
  @Prop()
  nextButtonEvent?: Function

  // 前へボタン押下時イベント
  @Prop()
  prevButtonEvent?: Function

  // 全問題
  @Prop()
  problems!: StudyProblem[]

  // 選択中の問題（問題毎に表示する場合のみ）
  @Prop()
  currentProblem?: StudyProblem

  // 数学キーボード名
  @Prop()
  mathKeyboardName!: string

  // 未入力問題番号
  @Prop()
  notAnsweredNumbers!: string[]

  // 学習完了確認時イベント
  @Prop()
  studyConfirmEvent!: Function

  // 学習完了時イベント
  @Prop()
  studyCompleteEvent!: Function

  // 学習終了時イベント
  @Prop()
  studyEndEvent!: Function

  // 一覧に戻るイベント
  @Prop()
  moveReturnEvent!: Function

  @Ref() modalStudyConfirm!: ModalStudyConfirm
  @Ref() modalStudySuspend!: ModalStudyConfirm
  @Ref() modalStudyComplete!: ModalStudyComplete

  // 縦書き/横書きコンポーネント名取得
  private get answerComponent(): string {
    return this.isVertical ? 'StudyBulkAnswerVertical' : 'StudyBulkAnswerHorizontal'
  }

  // 表示する問題一覧取得
  private get studyProblems(): StudyProblem[] {
    // 問題毎に全設問表示の場合は選択中の問題、全てを表示する場合は全ての問題
    return this.currentProblem ? [this.currentProblem] : this.problems
  }

  // 次へボタン表示フラグ
  private get showNextButton(): boolean {
    // 全ての問題を表示する場合は常に非表示
    if (!this.currentProblem) return false

    // 選択中の問題が全問題中最後の問題でなければ表示
    return this.currentProblem !== this.problems[this.problems.length - 1]
  }

  // 前へボタン表示フラグ
  private get showPrevButton(): boolean {
    // 全ての問題を表示する場合は常に非表示
    if (!this.currentProblem) return false

    // 選択中の問題が全問題中最初の問題でなければ表示
    return this.currentProblem !== this.problems[0]
  }

  // ヘッダーの開閉情報を更新
  private changeHeaderToggle(value: boolean) {
    this.isHeaderOpened = value
  }

  // 学習メニュー及び学習画面の終了ボタン押下時の処理
  private completeButtonEvent() {
    // 完了確認時イベントを実行
    this.studyConfirmEvent()
    // 完了確認モーダルを開く
    this.modalStudyConfirm.show()
  }

  // ヘッダーの戻るボタン押下時の処理
  private returnButtonEvent() {
    // 中断確認モーダルを開く
    this.modalStudySuspend.show()
  }

  // 学習完了確認処理
  private doStudyComplete() {
    // 確認処理を実行
    this.studyCompleteEvent()
    // 確認モーダルを閉じて完了モーダルを開く
    this.modalStudyConfirm.hide()
    this.modalStudyComplete.show()
  }
}
</script>

<style lang="stylus" scoped>
.study-bulk
  padding 0 10px

  &__content
    &__navigation
      padding 10px

    &__body
      display flex
      position relative
      // 100vhからヘッダー、ナビゲーション部の高さと余白10px分を引いた値
      height calc(100vh - 106px)

      &__answer
        width 100%

  &[data-header-opened='true'] &__content
    &__body
      // ヘッダーオープン時はヘッダーの高さが増える
      height calc(100vh - 152px)

  &[data-vertical='true'] &__content
    &__body
      flex-direction row-reverse
      text-orientation upright
</style>
